/**
 * @fileoverview MasterGroupRow — a single expandable row in the Master/Detail
 * cluster group table.
 *
 * Shows: Cluster Code badge, Unified Name (clickable → map drawer), Location
 * (City + Address), Merged indicator (clickable → merge-history drawer when the
 * cluster has had merges performed on it), and Merged Markets (which specific
 * markets were involved).
 *
 * Selecting a row highlights only the parent row; the expanded child panel is
 * styled independently so it does not inherit the hover/selected background.
 *
 * A merge is only ever possible between rows sharing the same `ClusterCode`.
 * When `selectionDisabled` is set (the active merge selection belongs to
 * another cluster), this row forwards it to {@link ClusterDetailPanel} so all
 * of its checkboxes render disabled.
 */

import * as React from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { getMarketType, ClusterCodeGroup } from '../utils';
import type { ClusterMergeHistoryEntry } from './MasterGroupedView';
import ClusterDetailPanel from './ClusterDetailPanel';
import LocationCell from './LocationCell';

// ---------------------------------------------------------------------------
// Column width constants
// ---------------------------------------------------------------------------

/**
 * Percentage widths for the parent (ClusterCode group) table columns.
 * Order: chevron, ClusterCode, UnifiedName, Location, Merged, MergedMarkets.
 */
export const MASTER_COL_WIDTHS = {
    chevron:       '4%',
    clusterCode:   '12%',
    unifiedName:   '27%',
    location:      '30%',
    mergedTo:      '10%',
    mergedMarkets: '17%',
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link MasterGroupRow}. */
export interface MasterGroupRowProps {
    /** The cluster group data to render. */
    group: ClusterCodeGroup;
    /** Whether this row is currently expanded. */
    expanded: boolean;
    /** Toggles the expanded state in the parent. */
    onToggle: () => void;
    /** Set of currently-checked mapping-row IDs (source of truth for all selection state). */
    checkedRowIds: Set<string>;
    /** Called when a single mapping row's checkbox is toggled. */
    onToggleRow: (id: string) => void;
    /**
     * Called when the UnifiedName cell is clicked to open the map-preview drawer.
     *
     * @param unifiedName      - Venue display name.
     * @param formattedAddress - Formatted address for the map embed.
     * @param googleMapsUri    - Google Maps URI.
     */
    onOpenDrawer: (unifiedName: string, formattedAddress: string, googleMapsUri: string) => void;
    /**
     * Called when the merge indicator is clicked to open the merge-history drawer.
     *
     * @param masterVenueId - The destination master venue ID to look up history for.
     */
    onOpenMergeHistory: (masterVenueId: number) => void;
    /**
     * Merge history entries for this cluster, loaded by the parent after expand.
     * Empty array means no merges have been performed for this cluster.
     */
    mergeHistory: ClusterMergeHistoryEntry[];
    /** Whether merge history is currently being fetched from the API. */
    mergeHistoryLoading: boolean;
    /**
     * `true` when the current merge selection is scoped to a *different*
     * `ClusterCode` group than this one — merges are only ever possible
     * between rows sharing the same `ClusterCode`, so this group's checkboxes
     * are disabled to make the constraint visible instead of only rejecting
     * the click after the fact.
     */
    selectionDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Builds a de-duplicated display string for all market types involved in the
 * given merge history entries.
 *
 * @param history - Merge history entries for this cluster.
 * @returns Comma-separated list of unique market-type labels.
 */
function buildMergedMarketLabels (history: ClusterMergeHistoryEntry[]): string[] {
    const seen = new Set<string>();
    history.forEach(entry => {
        entry.mergedMarkets.forEach(m => {
            const market = getMarketType(m.marketId, m.marketType);
            const label = `${market}:${m.sourceVenueId}`;
            seen.add(label);
        });
    });
    return Array.from(seen);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A single expandable row representing one {@link ClusterCodeGroup} in the
 * Master/Detail view.
 *
 * - **Unified Name** — clicking opens {@link UnifiedNameDrawer} via `onOpenDrawer`.
 * - **Merged** column — shows a merge icon when the cluster has merge history;
 *   clicking opens {@link MergeHistoryDrawer} via `onOpenMergeHistory`.
 * - **Merged Markets** — lists markets re-linked into this cluster via merges.
 * - Expanding the row reveals {@link ClusterDetailPanel} with per-PlaceId sub-groups.
 *
 * @param props.group               - The cluster group data.
 * @param props.expanded            - Whether the group is expanded.
 * @param props.onToggle            - Expansion toggle callback.
 * @param props.checkedRowIds       - Currently-checked mapping-row IDs.
 * @param props.onToggleRow         - Toggle callback for a single mapping row's checkbox.
 * @param props.onOpenDrawer        - Opens the map drawer.
 * @param props.onOpenMergeHistory  - Opens the merge-history drawer.
 * @param props.mergeHistory        - Merge history entries for this cluster.
 * @param props.mergeHistoryLoading - Whether history is loading.
 * @param props.selectionDisabled   - Disables this group's checkboxes when another cluster is actively selected.
 */
const MasterGroupRow: React.FC<MasterGroupRowProps> = ({
    group,
    expanded,
    onToggle,
    checkedRowIds,
    onToggleRow,
    onOpenDrawer,
    onOpenMergeHistory,
    mergeHistory,
    mergeHistoryLoading,
    selectionDisabled = false,
}) => {
    const { clusterCode, records } = group;
    const sampleRecord = records[0];
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const hasMergeHistory = mergeHistory.length > 0;

    /**
     * The destination master venue ID to open when the merge indicator is clicked.
     * Uses the first entry's destination as a representative.
     */
    const representativeDestinationId = hasMergeHistory
        ? mergeHistory[0].destinationMasterVenueId
        : null;

    /** De-duplicated list of market labels from all merge history entries. */
    const mergedMarketLabels = React.useMemo(
        () => buildMergedMarketLabels(mergeHistory),
        [mergeHistory],
    );

    return (
        <>
            {/* Parent row — highlighted when expanded; no highlight propagates to child rows */}
            <TableRow
                onClick={onToggle}
                sx={{
                    cursor: 'pointer',
                    bgcolor: expanded
                        ? isDark ? 'action.selected' : 'action.hover'
                        : undefined,
                    '&:hover': {
                        bgcolor: isDark ? 'action.selected' : 'action.hover',
                    },
                }}
                aria-expanded={expanded}
            >
                {/* Expand/collapse chevron */}
                <TableCell padding="none" sx={{ width: MASTER_COL_WIDTHS.chevron, pl: 0.5 }}>
                    <IconButton size="small" aria-label={expanded ? 'collapse group' : 'expand group'}>
                        {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                </TableCell>

                {/* Cluster Code badge */}
                <TableCell sx={{ width: MASTER_COL_WIDTHS.clusterCode }}>
                    {clusterCode ? (
                        <Chip
                            label={clusterCode}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.72rem' }}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            (no cluster code)
                        </Typography>
                    )}
                </TableCell>

                {/* Unified Name — clicking opens the map drawer */}
                <TableCell sx={{ width: MASTER_COL_WIDTHS.unifiedName }}>
                    {sampleRecord?.googleUnifiedName ? (
                        <Typography
                            variant="body2"
                            noWrap
                            component="span"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onOpenDrawer(
                                    sampleRecord.googleUnifiedName,
                                    sampleRecord.formattedAddress,
                                    sampleRecord.googleMapsUri,
                                );
                            }}
                            sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            {sampleRecord.googleUnifiedName}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                </TableCell>

                {/* Location: City, State + formatted address */}
                <TableCell sx={{ width: MASTER_COL_WIDTHS.location }}>
                    <LocationCell
                        city={sampleRecord?.city}
                        state={sampleRecord?.state}
                        formattedAddress={sampleRecord?.formattedAddress}
                    />
                </TableCell>

                {/* Merged indicator — shows when this cluster has had source venues merged into it */}
                <TableCell sx={{ width: MASTER_COL_WIDTHS.mergedTo }}>
                    {mergeHistoryLoading && !hasMergeHistory ? (
                        <CircularProgress size={14} thickness={4} sx={{ color: 'text.disabled' }} />
                    ) : hasMergeHistory ? (
                        <Tooltip
                            title={`${mergeHistory.length} merge(s) — click to view history`}
                            placement="top"
                        >
                            <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    if (representativeDestinationId) {
                                        onOpenMergeHistory(representativeDestinationId);
                                    }
                                }}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    color: 'warning.main',
                                    '&:hover': { color: 'warning.dark' },
                                }}
                                aria-label="View merge history"
                            >
                                <MergeTypeIcon fontSize="small" />
                                <Typography variant="caption" fontWeight={600}>
                                    {mergeHistory.length}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                </TableCell>

                {/* Merged Markets — lists market:venueId pairs from all merges in this cluster */}
                <TableCell sx={{ width: MASTER_COL_WIDTHS.mergedMarkets }}>
                    {mergedMarketLabels.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {mergedMarketLabels.map(label => (
                                <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontSize: '0.62rem', height: 18 }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                </TableCell>
            </TableRow>

            {/* Expand panel — explicitly neutral background to prevent inheriting parent highlight */}
            <TableRow
                sx={{
                    '& td': { borderBottom: expanded ? undefined : 0 },
                    '&:hover': { bgcolor: 'transparent' },
                }}
            >
                <TableCell
                    colSpan={6}
                    sx={{
                        p: 0,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                    }}
                >
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <ClusterDetailPanel
                            records={records}
                            checkedRowIds={checkedRowIds}
                            onToggleRow={onToggleRow}
                            onOpenDrawer={onOpenDrawer}
                            mergeHistory={mergeHistory}
                            selectionDisabled={selectionDisabled}
                        />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default MasterGroupRow;
